import { ConversationDetails, IssueCategory, ISSUE_CATEGORIES } from "@/types/conversation/conversation";

export type SentimentBucket = "positive" | "neutral" | "negative";

export interface SentimentMix {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
}

export interface PulseAggregates {
  total: number;
  thisPeriodCount: number;
  priorPeriodCount: number;
  volumeDelta: number;
  avgDurationSeconds: number | null;
  escalationCount: number;
  sentimentMix: SentimentMix;
  satisfactionAvg: number | null;
  agentTalkAvg: number | null;
}

export interface TopicTrend {
  topic: string;
  count: number;
  priorCount: number;
  delta: number;
  isNew: boolean;
}

export interface RecurringSignal {
  text: string;
  count: number;
  kind: "insight" | "recommendation";
}

export interface AttentionCall {
  id: string | null;
  fullPageId: string | null;
  genieName: string;
  date: string | null;
  reason: string;
  reasonKind: "negative" | "escalation" | "no-analysis" | "no-lead";
  durationSeconds: number | null;
}

export interface ResolutionStats {
  resolved: number;
  partial: number;
  unresolved: number;
  classified: number;
  resolutionRate: number | null;
}

export interface CategoryBreakdownEntry {
  category: IssueCategory;
  count: number;
}

export interface TimeDistribution {
  byHour: number[];
  byWeekday: number[];
  peakHour: number | null;
  peakWeekday: number | null;
}

const WEEKDAY_COUNT = 7;
const HOUR_COUNT = 24;

const PERIOD_DAYS: Record<string, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: 365,
};

const getSentimentBucket = (c: ConversationDetails): SentimentBucket => {
  const raw = c.analysis_result?.sentiment_analysis?.overall_sentiment_type;
  if (raw === "positive" || raw === "negative") return raw;
  return "neutral";
};

const inPeriod = (c: ConversationDetails, startMs: number, endMs: number) => {
  if (!c.conversation_date) return false;
  const t = new Date(c.conversation_date).getTime();
  return t >= startMs && t < endMs;
};

export const buildPulseAggregates = (
  conversations: ConversationDetails[],
  dateRange: string,
): PulseAggregates => {
  const total = conversations.length;
  const days = PERIOD_DAYS[dateRange] ?? 30;
  const now = Date.now();
  const periodMs = days * 24 * 60 * 60 * 1000;
  const thisStart = now - periodMs;
  const priorStart = now - 2 * periodMs;

  const thisPeriod = conversations.filter((c) => inPeriod(c, thisStart, now));
  const priorPeriod = conversations.filter((c) => inPeriod(c, priorStart, thisStart));

  const thisPeriodCount = thisPeriod.length;
  const priorPeriodCount = priorPeriod.length;
  const volumeDelta = priorPeriodCount === 0
    ? thisPeriodCount > 0 ? 100 : 0
    : Math.round(((thisPeriodCount - priorPeriodCount) / priorPeriodCount) * 100);

  const durationSamples = conversations
    .map((c) => c.duration_seconds)
    .filter((d): d is number => typeof d === "number" && d > 0);
  const avgDurationSeconds = durationSamples.length
    ? Math.round(durationSamples.reduce((a, b) => a + b, 0) / durationSamples.length)
    : null;

  const escalationCount = conversations.filter((c) => !!c.escalation).length;

  const mix: SentimentMix = { positive: 0, neutral: 0, negative: 0, total: 0 };
  for (const c of conversations) {
    if (!c.analysis_result?.sentiment_analysis) continue;
    mix[getSentimentBucket(c)] += 1;
    mix.total += 1;
  }

  const satisfactionSamples = conversations
    .map((c) => c.analysis_result?.sentiment_analysis?.customer_satisfaction_score)
    .filter((s): s is number => typeof s === "number");
  const satisfactionAvg = satisfactionSamples.length
    ? Math.round(satisfactionSamples.reduce((a, b) => a + b, 0) / satisfactionSamples.length)
    : null;

  const agentTalkSamples = conversations
    .map((c) => c.analysis_result?.talk_metrics?.agent_talk_percentage)
    .filter((s): s is number => typeof s === "number");
  const agentTalkAvg = agentTalkSamples.length
    ? Math.round(agentTalkSamples.reduce((a, b) => a + b, 0) / agentTalkSamples.length)
    : null;

  return {
    total,
    thisPeriodCount,
    priorPeriodCount,
    volumeDelta,
    avgDurationSeconds,
    escalationCount,
    sentimentMix: mix,
    satisfactionAvg,
    agentTalkAvg,
  };
};

export const buildTopicTrends = (
  conversations: ConversationDetails[],
  dateRange: string,
  limit = 8,
): TopicTrend[] => {
  const days = PERIOD_DAYS[dateRange] ?? 30;
  const now = Date.now();
  const periodMs = days * 24 * 60 * 60 * 1000;
  const thisStart = now - periodMs;
  const priorStart = now - 2 * periodMs;

  const tally = (subset: ConversationDetails[]) => {
    const map = new Map<string, number>();
    for (const c of subset) {
      const topics = c.topics || c.analysis_result?.topics || c.analysis_result?.main_topics || [];
      for (const raw of topics) {
        if (typeof raw !== "string") continue;
        const topic = raw.trim();
        if (!topic) continue;
        map.set(topic, (map.get(topic) || 0) + 1);
      }
    }
    return map;
  };

  const thisMap = tally(conversations.filter((c) => inPeriod(c, thisStart, now)));
  const priorMap = tally(conversations.filter((c) => inPeriod(c, priorStart, thisStart)));
  const hasPriorContext = priorMap.size > 0;

  const trends: TopicTrend[] = Array.from(thisMap.entries()).map(([topic, count]) => {
    const priorCount = priorMap.get(topic) || 0;
    const delta = priorCount === 0 ? count : count - priorCount;
    return {
      topic,
      count,
      priorCount,
      delta,
      // Only flag as "new" if we have prior-period data to compare against —
      // otherwise every topic looks "new" and the badge is noise.
      isNew: hasPriorContext && priorCount === 0,
    };
  });

  trends.sort((a, b) => b.count - a.count);
  return trends.slice(0, limit);
};

export const buildResolutionStats = (conversations: ConversationDetails[]): ResolutionStats => {
  const stats = { resolved: 0, partial: 0, unresolved: 0 };
  for (const c of conversations) {
    const status = c.analysis_result?.resolution_status;
    if (status === "resolved" || status === "partial" || status === "unresolved") {
      stats[status] += 1;
    }
  }
  const classified = stats.resolved + stats.partial + stats.unresolved;
  const resolutionRate = classified > 0 ? Math.round((stats.resolved / classified) * 100) : null;
  return { ...stats, classified, resolutionRate };
};

export const buildCategoryBreakdown = (
  conversations: ConversationDetails[],
  limit = 8,
): CategoryBreakdownEntry[] => {
  const counts = new Map<IssueCategory, number>();
  for (const c of conversations) {
    const category = c.analysis_result?.issue_category as IssueCategory | undefined;
    if (!category || !ISSUE_CATEGORIES.includes(category)) continue;
    counts.set(category, (counts.get(category) || 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([category, count]) => ({ category, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
};

export const buildTimeDistribution = (conversations: ConversationDetails[]): TimeDistribution => {
  const byHour = new Array(HOUR_COUNT).fill(0);
  const byWeekday = new Array(WEEKDAY_COUNT).fill(0);

  for (const c of conversations) {
    if (!c.conversation_date) continue;
    const date = new Date(c.conversation_date);
    if (isNaN(date.getTime())) continue;
    byHour[date.getHours()] += 1;
    byWeekday[date.getDay()] += 1;
  }

  const peakHour = byHour.some((n) => n > 0) ? byHour.indexOf(Math.max(...byHour)) : null;
  const peakWeekday = byWeekday.some((n) => n > 0) ? byWeekday.indexOf(Math.max(...byWeekday)) : null;

  return { byHour, byWeekday, peakHour, peakWeekday };
};

const normalise = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const buildRecurringSignals = (
  conversations: ConversationDetails[],
  limit = 6,
): RecurringSignal[] => {
  type Bucket = {
    kind: "insight" | "recommendation";
    text: string;
    count: number;
    latestTs: number;
  };
  const buckets = new Map<string, Bucket>();

  for (const c of conversations) {
    const ts = c.conversation_date ? new Date(c.conversation_date).getTime() : 0;

    const collect = (items: unknown[], kind: "insight" | "recommendation") => {
      for (const item of items) {
        if (typeof item !== "string") continue;
        const text = item.trim();
        if (text.length < 8) continue;
        const prefix = normalise(text).split(" ").slice(0, 6).join(" ");
        if (!prefix) continue;
        const key = `${kind}:${prefix}`;
        const existing = buckets.get(key);
        if (existing) {
          existing.count += 1;
          if (ts > existing.latestTs) existing.latestTs = ts;
        } else {
          buckets.set(key, { kind, text, count: 1, latestTs: ts });
        }
      }
    };

    collect(c.analysis_result?.key_insights || [], "insight");
    collect(c.analysis_result?.recommendations || [], "recommendation");
  }

  const signals = Array.from(buckets.values());
  // Clusters first, then most recent — guarantees the panel is populated whenever
  // any conversation has been analysed, while still floating real patterns to the top.
  signals.sort((a, b) => b.count - a.count || b.latestTs - a.latestTs);
  return signals.slice(0, limit).map(({ kind, text, count }) => ({ kind, text, count }));
};

export const buildAttentionQueue = (
  conversations: ConversationDetails[],
  limit = 6,
): AttentionCall[] => {
  const out: AttentionCall[] = [];

  for (const c of conversations) {
    const sentiment = c.analysis_result?.sentiment_analysis?.overall_sentiment_type;
    if (sentiment === "negative") {
      out.push({
        id: c.elevenlabs_conversation_id || null,
        fullPageId: c.conversation_id != null ? String(c.conversation_id) : null,
        genieName: c.genie_name || "Unknown Genie",
        date: c.conversation_date || null,
        reason: "Negative sentiment — review and follow up",
        reasonKind: "negative",
        durationSeconds: c.duration_seconds ?? null,
      });
    }
  }

  for (const c of conversations) {
    if (!c.escalation) continue;
    out.push({
      id: c.elevenlabs_conversation_id || null,
      fullPageId: c.conversation_id != null ? String(c.conversation_id) : null,
      genieName: c.genie_name || "Unknown Genie",
      date: c.conversation_date || null,
      reason: "Escalation triggered — needs review",
      reasonKind: "escalation",
      durationSeconds: c.duration_seconds ?? null,
    });
  }

  for (const c of conversations) {
    const hasAnalysis = c.has_analysis ?? !!c.analysis_result;
    if (hasAnalysis) continue;
    if ((c.duration_seconds ?? 0) < 30) continue;
    out.push({
      id: c.elevenlabs_conversation_id || null,
      fullPageId: c.conversation_id != null ? String(c.conversation_id) : null,
      genieName: c.genie_name || "Unknown Genie",
      date: c.conversation_date || null,
      reason: "Awaiting analysis — run to unlock insights",
      reasonKind: "no-analysis",
      durationSeconds: c.duration_seconds ?? null,
    });
  }

  for (const c of conversations) {
    if (!c.analysis_result) continue;
    const leadCount = c.lead_info ? Object.keys(c.lead_info).length : 0;
    if (leadCount > 0) continue;
    if ((c.duration_seconds ?? 0) < 60) continue;
    out.push({
      id: c.elevenlabs_conversation_id || null,
      fullPageId: c.conversation_id != null ? String(c.conversation_id) : null,
      genieName: c.genie_name || "Unknown Genie",
      date: c.conversation_date || null,
      reason: "No lead details captured — check goal prompts",
      reasonKind: "no-lead",
      durationSeconds: c.duration_seconds ?? null,
    });
  }

  const seen = new Set<string>();
  const deduped: AttentionCall[] = [];
  for (const item of out) {
    const key = `${item.id}-${item.reasonKind}`;
    if (seen.has(key)) continue;
    seen.add(key);
    deduped.push(item);
  }

  return deduped.slice(0, limit);
};

export const formatRelativeDate = (dateString: string | null): string => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  const diff = Date.now() - date.getTime();
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};
